/**
 * Themes autocomplete filter component
 * Shadcn-inspired autocomplete with multi-select
 * Uses theme colours from @uicwebmaster/taxonomy-themes for visual distinction
 * Displays themes with hierarchical structure
 * Shared component for @uicwebmaster/events, @uicwebmaster/articles, etc.
 *
 * OPTIMISATION NOTE:
 * This component now expects pre-enriched themes from parent hooks.
 * Theme enrichment (ancestors) is handled by use-events-filter or use-applications-filter.
 * This eliminates duplicate enrichment logic and improves performance.
 */

'use client';

import { Check, ChevronDown, Tags, X } from 'lucide-react';
import { useMemo, useState, type ComponentType } from 'react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * Theme interface for filter components
 * Compatible with ThemeBadgeTheme from @uicwebmaster/events
 */
export interface Theme {
  id: string;
  name: string;
  color?: string | null;
  level?: number;
  children?: Theme[];
}

/**
 * Props for ThemeBadge component (compatible with @uicwebmaster/events ThemeBadge)
 */
export interface ThemeBadgeProps {
  theme: { id: string; name: string; color?: string | null };
  variant?: 'default' | 'compact' | 'filter';
  removable?: boolean;
  onRemove?: (themeId: string) => void;
  className?: string;
}

/**
 * Simple ThemeBadge component for use in filters
 * Can be overridden by passing a custom ThemeBadge component via props
 */
function DefaultThemeBadge({
  theme,
  variant = 'default',
  removable = false,
  onRemove,
  className = '',
}: ThemeBadgeProps) {
  const baseClasses = 'inline-flex items-center gap-1 rounded-full border';
  const variantClasses = {
    default: 'bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary',
    compact: 'bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary',
    filter: 'bg-background px-2 py-0.5 text-xs font-medium text-primary',
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove(theme.id);
    }
  };

  return (
    <span
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={{
        borderColor: theme.color || 'currentColor',
      }}
    >
      {theme.name}
      {removable && onRemove && (
        <span
          onClick={handleRemove}
          className="hover:bg-primary/20 rounded-full cursor-pointer inline-flex"
          role="button"
          aria-label={`Remove ${theme.name}`}
        >
          <X className="h-3 w-3" />
        </span>
      )}
    </span>
  );
}

/**
 * Flatten theme tree to array while preserving hierarchical order
 */
function flattenThemeTree(themes: Theme[]): Theme[] {
  // Safety check
  if (!themes || !Array.isArray(themes)) {
    console.warn('[flattenThemeTree] Invalid themes input:', themes);
    return [];
  }

  const result: Theme[] = [];

  function traverse(theme: Theme) {
    result.push(theme);
    if (theme.children && theme.children.length > 0) {
      theme.children.forEach(traverse);
    }
  }

  themes.forEach(traverse);
  return result;
}

/**
 * Get all descendant IDs of a theme (children, grandchildren, etc.)
 * @param themeId - ID of the parent theme
 * @param themesTree - Hierarchical theme tree
 * @returns Array of all descendant theme IDs
 */
function getAllDescendantIds(themeId: string, themesTree: Theme[]): string[] {
  const descendantIds: string[] = [];

  // Find the theme in the tree
  function findTheme(themes: Theme[]): Theme | null {
    for (const theme of themes) {
      if (theme.id === themeId) {
        return theme;
      }
      if (theme.children && theme.children.length > 0) {
        const found = findTheme(theme.children);
        if (found) return found;
      }
    }
    return null;
  }

  const theme = findTheme(themesTree);
  if (!theme) return descendantIds;

  // Recursively collect all descendant IDs
  function collectDescendants(t: Theme): void {
    if (t.children && t.children.length > 0) {
      t.children.forEach(child => {
        descendantIds.push(child.id);
        collectDescendants(child);
      });
    }
  }

  collectDescendants(theme);
  return descendantIds;
}

interface FilterThemesAutocompleteProps {
  /** Pre-enriched themes tree (with ancestors) - provided by parent hook */
  themes: Theme[];
  selectedThemes: string[];
  onToggleTheme: (themeId: string) => void;
  /** Optional: Toggle multiple themes at once (for hierarchical selection) */
  onToggleThemes?: (themeIds: string[], shouldSelect?: boolean) => void;
  onClearSelection: () => void;
  placeholder?: string;
  /** Optional custom ThemeBadge component (from @uicwebmaster/events) */
  ThemeBadge?: ComponentType<ThemeBadgeProps>;
  /**
   * @deprecated No longer used - themes should be pre-enriched by parent hook
   * Kept for backwards compatibility but has no effect
   */
  allThemes?: Array<{ id: string; name: string; color?: string | null; parentId?: string }>;
}

export function FilterThemesAutocomplete({
  themes,
  selectedThemes,
  onToggleTheme,
  onToggleThemes,
  onClearSelection,
  placeholder = 'Select themes...',
  ThemeBadge: CustomThemeBadge,
  allThemes: providedAllThemes,
}: FilterThemesAutocompleteProps) {
  // Use custom ThemeBadge if provided, otherwise use default
  const ThemeBadgeComponent = CustomThemeBadge || DefaultThemeBadge;
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  /**
   * Reasoning Path:
   * 1. Themes are now pre-enriched by parent hooks (use-events-filter, use-applications-filter)
   * 2. No need for additional enrichment here - simply use provided themes
   * 3. This reduces complexity and improves performance by eliminating duplicate enrichment
   * 4. Parent hooks handle ancestor collection - we just display the hierarchical structure
   */
  const displayThemes = useMemo(() => {
    // Themes are already enriched with ancestors by parent hook
    // Simply validate and return them
    if (!themes || !Array.isArray(themes)) {
      console.warn('[FilterThemesAutocomplete] Invalid themes provided:', themes);
      return [];
    }

    return themes;
  }, [themes]);

  /**
   * Handle theme toggle with hierarchical logic
   * When toggling a parent, also toggle all its descendants
   */
  const handleToggleTheme = (themeId: string) => {
    // Get all descendant IDs
    const descendantIds = getAllDescendantIds(themeId, displayThemes);

    // Check if theme is currently selected
    const isCurrentlySelected = selectedThemes.includes(themeId);

    if (descendantIds.length > 0) {
      // This is a parent theme - toggle it and all descendants
      // Use onToggleThemes if available (more efficient), otherwise fall back to multiple calls
      const allIds = [themeId, ...descendantIds];

      if (onToggleThemes) {
        // Efficient: toggle all at once
        onToggleThemes(allIds, !isCurrentlySelected);
      } else {
        // Fallback: multiple toggle calls
        if (isCurrentlySelected) {
          // Unselect parent and all descendants
          onToggleTheme(themeId);
          descendantIds.forEach(id => {
            if (selectedThemes.includes(id)) {
              onToggleTheme(id);
            }
          });
        } else {
          // Select parent and all descendants
          onToggleTheme(themeId);
          descendantIds.forEach(id => {
            if (!selectedThemes.includes(id)) {
              onToggleTheme(id);
            }
          });
        }
      }
    } else {
      // This is a leaf theme - just toggle it
      onToggleTheme(themeId);
    }
  };

  // Flatten themes tree to preserve hierarchical order
  const flatThemes = useMemo(() => {
    return flattenThemeTree(displayThemes);
  }, [displayThemes]);

  // Filter themes based on search query
  const filteredThemes = useMemo(() => {
    if (!searchQuery) return flatThemes;
    const query = searchQuery.toLowerCase();
    return flatThemes.filter(theme => theme.name.toLowerCase().includes(query));
  }, [flatThemes, searchQuery]);

  // Get selected theme names and colours
  const selectedThemeData = useMemo(() => {
    return flatThemes.filter(t => selectedThemes.includes(t.id));
  }, [flatThemes, selectedThemes]);

  const hasSelection = selectedThemes.length > 0;

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-auto min-h-[2.5rem] py-2 !bg-transparent border-text-muted-foreground border-1"
          >
            <div className="flex flex-wrap gap-1 flex-1 mr-2">
              <Tags className="h-4 w-4 mt-0.5 mr-1 text-muted-foreground" />
              {hasSelection ? (
                <>
                  {selectedThemeData.slice(0, 2).map(theme => (
                    <ThemeBadgeComponent
                      key={theme.id}
                      theme={theme}
                      variant="filter"
                      removable={true}
                      onRemove={(id: string) => handleToggleTheme(id)}
                    />
                  ))}
                  {selectedThemes.length > 2 && (
                    <span className="inline-flex items-center rounded-full bg-background px-2 py-0.5 text-xs font-medium text-primary">
                      +{selectedThemes.length - 2} more
                    </span>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground text-sm font-normal">{placeholder}</span>
              )}
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 rounded border-2 shadow-lg"
          align="start"
        >
          <div className="flex flex-col">
            {/* Search input */}
            <div className="border-b p-2 bg-background">
              <input
                type="text"
                placeholder="Search themes..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full rounded-md border-0 bg-background px-2 py-1.5 text-sm outline-none placeholder:text-muted-foreground focus:ring-0"
              />
            </div>

            {/* Clear button */}
            {hasSelection && (
              <div className="border-b p-2 bg-background">
                <button
                  type="button"
                  onClick={() => {
                    onClearSelection();
                    setSearchQuery('');
                  }}
                  className="w-full text-left text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear selection ({selectedThemes.length})
                </button>
              </div>
            )}

            {/* Themes list */}
            <div className="max-h-[300px] overflow-y-auto bg-background">
              {filteredThemes.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground bg-background">
                  No themes found
                </div>
              ) : (
                <div className="p-1 bg-background">
                  {filteredThemes.map(theme => {
                    const isSelected = selectedThemes.includes(theme.id);
                    const level = theme.level ?? 0;
                    const indentationPx = level * 16; // 16px per level

                    return (
                      <button
                        key={theme.id}
                        type="button"
                        onClick={() => handleToggleTheme(theme.id)}
                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                        style={{
                          paddingLeft: `${8 + indentationPx}px`,
                        }}
                      >
                        <div
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border-2 ${
                            isSelected ? 'bg-primary text-primary-foreground' : ''
                          }`}
                          style={{
                            borderColor: theme.color || 'currentColor',
                          }}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </div>
                        <span
                          className={`flex-1 text-left ${level === 0 ? 'font-semibold' : level === 1 ? 'font-medium' : ''}`}
                        >
                          {theme.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
